/**
 * React Hook for Theme Management
 * 
 * Provides a complete theme management solution with:
 * - Light/dark mode switching
 * - System preference detection
 * - Local storage persistence
 * - Automatic system theme change detection
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  initializeTheme, 
  createSystemThemeListener, 
  createThemeContextValue,
  applyTheme,
  toggleTheme as toggleThemeUtil,
  type ThemeContextValue 
} from '../styles/theme';
import { type Theme } from '../styles/colors';

/**
 * Custom hook for theme management
 */
export const useTheme = (): ThemeContextValue => {
  const [theme, setTheme] = useState<Theme>(() => initializeTheme());

  // Apply dark class to document root for Tailwind CSS
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const cleanup = createSystemThemeListener((newTheme) => {
      setTheme(newTheme);
    });

    return cleanup;
  }, []);

  // Memoized theme switching functions
  const handleToggleTheme = useCallback(() => {
    const newTheme = toggleThemeUtil();
    setTheme(newTheme);
    return newTheme;
  }, []);

  const handleSetTheme = useCallback((newTheme: Theme) => {
    applyTheme(newTheme);
    setTheme(newTheme);
  }, []);

  // Return theme context value
  return {
    ...createThemeContextValue(theme),
    toggle: handleToggleTheme,
    setTheme: handleSetTheme,
  };
};

/**
 * THUB-298: `useThemeCSS` and `useAccessibilityPreferences` were deleted here rather than
 * refactored.
 *
 * `useAccessibilityPreferences` was the last react-hooks/set-state-in-effect finding that looked
 * like a legitimate external-system subscription -- it read window.matchMedia in an effect and
 * setState'd, which the rule flags even though the pattern is correct React. The textbook fix is
 * useSyncExternalStore, and I had started writing it before checking whether the hook runs at
 * all: it has ZERO consumers, and its marker string 'prefers-contrast' has zero occurrences in
 * the shipped bundle. Same for useThemeCSS ('textInteractive', 'actionPrimaryHover', 'bgSurface'
 * -- all absent). Both are tree-shaken away.
 *
 * Rewriting dead code to satisfy a lint rule is the SearchFilters mistake from earlier in this
 * slate, so: deleted. Note THUB-296's reachability guard cannot catch this class -- it works at
 * FILE granularity, and these were dead EXPORTS inside a live module. Dead-export detection wants
 * its own ticket.
 *
 * If accessibility preferences are wanted later, `styles/theme.ts` already exports a
 * `prefersReducedMotion()` helper, and the hook version should use useSyncExternalStore.
 */

/**
 * Example usage:
 * 
 * // Basic theme switching
 * const MyComponent = () => {
 *   const { theme, isDark, toggle } = useTheme();
 *   
 *   return (
 *     <div>
 *       <p>Current theme: {theme}</p>
 *       <button onClick={toggle}>
 *         Switch to {isDark ? 'light' : 'dark'} mode
 *       </button>
 *     </div>
 *   );
 * };
 * 
 * // Using CSS variables
 * const StyledComponent = () => {
 *   const { css } = useThemeCSS();
 *   
 *   return (
 *     <div style={{
 *       backgroundColor: css.bgPrimary,
 *       color: css.textPrimary,
 *       border: `1px solid ${css.borderDefault}`,
 *     }}>
 *       Theme-aware content
 *     </div>
 *   );
 * };
 * 
 * // Accessibility-aware animations
 * const AnimatedComponent = () => {
 *   const { transitionDuration } = useAccessibilityPreferences();
 *   
 *   return (
 *     <div style={{
 *       transition: `all ${transitionDuration} ease`,
 *     }}>
 *       Respects user motion preferences
 *     </div>
 *   );
 * };
 */